import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { ListLeadsParams } from "../types";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import { useCompanyId } from "@/context/CompanyContext";
import { leadsKeys, type LeadsPageKeyInput } from "./keys";

/** Filters managed by the leads list UI. */
export interface LeadsFilters {
  /** Free-text search (matched server-side on display name/email). */
  q: string;
  /** Lead status filter — empty string means all statuses. */
  status: string;
}

export const DEFAULT_LEADS_FILTERS: LeadsFilters = {
  q: "",
  status: "",
};

/**
 * Server cap we request. The backend allows up to 500 — if the response is
 * full we surface "500+" in the count so the user knows results are truncated.
 */
export const LEADS_FETCH_LIMIT = 500;

const SEARCH_DEBOUNCE_MS = 200;

/** Serialise list filters into a stable TanStack Query cache key fragment. */
export function buildLeadsFiltersKey(filters: LeadsFilters): string {
  return JSON.stringify({
    q: filters.q.trim().toLowerCase(),
    s: filters.status,
  });
}

/** Build the full leads-page cache key input from list filters. */
export function buildLeadsPageKeyInput(
  filters: LeadsFilters,
  companyId?: string,
): LeadsPageKeyInput {
  return {
    filtersKey: buildLeadsFiltersKey(filters),
    ...(companyId ? { companyId } : {}),
  };
}

function filtersToApiParams(filters: LeadsFilters): ListLeadsParams {
  const apiParams: ListLeadsParams = { limit: LEADS_FETCH_LIMIT };
  if (filters.q.trim()) apiParams.q = filters.q.trim();
  if (filters.status) apiParams.status = filters.status;
  return apiParams;
}

export interface UseLeadsPageOptions {
  filters: LeadsFilters;
  enabled?: boolean;
}

/**
 * Cached leads list for the Leads module page.
 * Pauses fetching when the OS window is minimised, unfocused, or in a background workspace.
 */
export function useLeadsPage({
  filters,
  enabled: enabledOption = true,
}: UseLeadsPageOptions) {
  const isWindowLive = useWindowLive();
  const companyId = useCompanyId();
  const queryEnabled = enabledOption && isWindowLive;
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedFilters(filters),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [filters]);

  const keyInput = useMemo(
    () => buildLeadsPageKeyInput(debouncedFilters, companyId ?? undefined),
    [companyId, debouncedFilters],
  );
  const searching = debouncedFilters.q.trim() !== "";

  const query = useQuery({
    queryKey: leadsKeys.page(keyInput),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    placeholderData: (prev) => (searching ? undefined : prev),
    queryFn: () => AgenticV2API.listLeads(filtersToApiParams(debouncedFilters)),
  });

  const leads = query.data ?? [];
  const loading = query.isLoading && leads.length === 0;
  const refetching = query.isFetching && !loading;
  const hasMore = leads.length >= LEADS_FETCH_LIMIT;

  return {
    leads,
    loading,
    refetching,
    error: query.error,
    hasMore,
    refresh: () => void query.refetch(),
    keyInput,
  };
}

/** Distinct lead statuses for the Leads list filter chips (tenant-scoped). */
export function useLeadStatuses({ enabled: enabledOption = true }: { enabled?: boolean } = {}) {
  const isWindowLive = useWindowLive();
  const companyId = useCompanyId();
  const queryEnabled = enabledOption && isWindowLive;

  const query = useQuery({
    queryKey: leadsKeys.statuses(companyId ?? undefined),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    queryFn: () => AgenticV2API.getLeadStatuses(),
  });

  return {
    statuses: query.data?.statuses ?? [],
    loading: query.isLoading,
    error: query.error,
    refresh: () => void query.refetch(),
  };
}
